import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { useEliteStructure } from "@/contexts/EliteTemporaryContextProvider";
import { useGuestAuth } from "@/contexts/GuestAuthContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  ArrowLeft,
  CheckCircle2,
  MailCheck,
  RefreshCw,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export default function GuestAreaVerify() {
  const { config, profile, verifyEmail, resendVerification } = useGuestAuth();
  const { structure } = useEliteStructure();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useDocumentTitle("Conferma Email Guest Area");

  const queryStructureId = Number(searchParams.get("structureId") || 0);
  const queryEmail = searchParams.get("email") || "";
  const queryMagicToken = searchParams.get("magicToken") || "";

  const effectiveStructureId =
    queryStructureId ||
    config?.structureId ||
    structure?.id ||
    Number(localStorage.getItem("structureId") || 0);

  const [email, setEmail] = useState(queryEmail);
  const [otpCode, setOtpCode] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [verifyingMagic, setVerifyingMagic] = useState(Boolean(queryMagicToken));
  const magicAttemptedRef = useRef(false);

  useEffect(() => {
    if (queryEmail && !email) {
      setEmail(queryEmail);
    }
  }, [queryEmail]);

  useEffect(() => {
    if (profile && !verifyingMagic) {
      navigate("/guest-area", { replace: true });
    }
  }, [profile, verifyingMagic, navigate]);

  useEffect(() => {
    if (!queryMagicToken || magicAttemptedRef.current) return;
    magicAttemptedRef.current = true;

    setVerifyingMagic(true);
    setErrorMessage(null);

    verifyEmail({
      structureId: effectiveStructureId,
      email: queryEmail || undefined,
      magicToken: queryMagicToken,
    })
      .then(() => {
        toast.success("Email confermata. Benvenuto nella Guest Area!");
        navigate("/guest-area", { replace: true });
      })
      .catch((err: any) => {
        const msg =
          err?.response?.data?.message ||
          "Il link di conferma non è valido o è scaduto. Inserisci il codice OTP oppure richiedi un nuovo invio.";
        setErrorMessage(msg);
        toast.error(msg);
      })
      .finally(() => {
        setVerifyingMagic(false);
      });
  }, [queryMagicToken, effectiveStructureId, queryEmail, verifyEmail, navigate]);

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setInfoMessage(null);

    const cleanOtp = otpCode.trim().replace(/\s+/g, "");
    if (!email.trim()) {
      setErrorMessage("Inserisci l'indirizzo email con cui ti sei registrato.");
      return;
    }

    if (cleanOtp.length !== 6) {
      setErrorMessage("Inserisci il codice OTP a 6 cifre ricevuto via email.");
      return;
    }

    setSubmitting(true);
    try {
      await verifyEmail({
        structureId: effectiveStructureId,
        email: email.trim(),
        otpCode: cleanOtp,
      });
      toast.success("Account confermato con successo!");
      navigate("/guest-area", { replace: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        "Il codice OTP inserito non è valido. Controlla l'email e riprova.";
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email.trim()) {
      setErrorMessage("Inserisci il tuo indirizzo email per ricevere un nuovo codice.");
      return;
    }

    setErrorMessage(null);
    setInfoMessage(null);
    setResending(true);
    try {
      const msg = await resendVerification(email.trim(), effectiveStructureId);
      setInfoMessage(msg);
      toast.success(msg);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        "Impossibile inviare nuovamente l'email di verifica.";
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  if (verifyingMagic) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="max-w-md mx-auto py-16 px-4 text-center space-y-4"
      >
        <Spinner className="mx-auto size-8 text-primary" />
        <div className="space-y-1">
          <h1 className="text-xl font-bold text-foreground">
            Verifica del Magic Link in corso...
          </h1>
          <p className="text-sm text-muted-foreground">
            Stiamo attivando il tuo account Guest Area.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-4 sm:py-8 space-y-6">
      <div>
        <Button
          asChild
          variant="ghost"
          className="min-h-11 px-3 -ml-3 text-muted-foreground hover:text-foreground gap-2"
        >
          <Link to="/guest-area/login">
            <ArrowLeft className="size-4" aria-hidden="true" />
            <span>Torna al login</span>
          </Link>
        </Button>
      </div>

      <section aria-labelledby="verify-heading" className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="text-xs font-medium gap-1.5">
            <MailCheck className="size-3.5 text-primary" aria-hidden="true" />
            <span>Verifica indirizzo email</span>
          </Badge>
        </div>

        <h1
          id="verify-heading"
          className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground"
        >
          Conferma il tuo account
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Abbiamo inviato un&apos;email{email ? ` a ${email}` : ""} con un{" "}
          <strong className="font-semibold text-foreground">
            codice OTP a 6 cifre
          </strong>{" "}
          e un{" "}
          <strong className="font-semibold text-foreground">Magic Link</strong>{" "}
          di conferma. Inserisci il codice qui sotto oppure clicca direttamente
          sul link ricevuto via email.
        </p>
      </section>

      <Card className="border border-border bg-card shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">
            Inserisci il codice OTP
          </CardTitle>
          <CardDescription className="text-xs">
            Controlla anche la cartella spam o promozioni se non trovi il
            messaggio.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleVerifyOtp} className="space-y-4" noValidate>
            {errorMessage && (
              <div
                role="alert"
                className="rounded-lg border border-destructive/40 bg-destructive/10 px-3.5 py-2.5 text-xs sm:text-sm font-medium text-destructive"
              >
                {errorMessage}
              </div>
            )}

            {infoMessage && (
              <div
                role="status"
                className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3.5 py-2.5 text-xs sm:text-sm font-medium text-emerald-600 dark:text-emerald-400"
              >
                {infoMessage}
              </div>
            )}

            {!queryEmail && (
              <div className="space-y-1.5">
                <Label htmlFor="guest-verify-email">Email</Label>
                <Input
                  id="guest-verify-email"
                  type="email"
                  autoComplete="email"
                  placeholder="email@esempio.it"
                  className="min-h-11"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="guest-verify-otp">Codice OTP (6 cifre)</Label>
              <Input
                id="guest-verify-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                className="min-h-12 text-center font-mono text-xl font-bold tracking-[0.35em]"
                value={otpCode}
                onChange={(e) =>
                  setOtpCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
                }
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full min-h-11 font-semibold gap-2 cursor-pointer"
              disabled={submitting}
            >
              <CheckCircle2 className="size-4" aria-hidden="true" />
              <span>
                {submitting
                  ? "Verifica in corso..."
                  : "Conferma e accedi alla Guest Area"}
              </span>
            </Button>
          </form>

          <div className="pt-2 border-t border-border flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              Non hai ricevuto l&apos;email?
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-10 gap-1.5 text-xs"
              onClick={handleResend}
              disabled={resending}
            >
              <RefreshCw
                className={`size-3.5 ${resending ? "animate-spin" : ""}`}
                aria-hidden="true"
              />
              <span>
                {resending ? "Invio in corso..." : "Invia di nuovo il codice"}
              </span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
